import { Avatar, Box, Card, CircularProgress, Typography } from '@mui/material';
import { Count, useGetIdentity, useTranslate } from 'react-admin';
import LastUpdated from '../../components/LastUpdated';
import { useUser } from '../../providers/userProvider';
import { customPalette } from '../../themes/customTheme';
import { hasRole } from '../../utils/roles';
import StatusCircle from './StatusCircle';
import TrendingIndicator from './TrendingIndicator';

const MoreInformation = () => {
  const t = useTranslate();
  const { totalMembers, userData, teamName, loading: userLoading } = useUser();
  if (userLoading) {
    return <CircularProgress />;
  }

  return (
    <Box display={'flex'} flexDirection="row" alignItems="center" gap="32px">
      {hasRole(['admin', 'gerente']) ? (
        <Box
          variant="h3"
          sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}
        >
          <Typography
            variant="span"
            sx={{
              fontSize: '18px',
              fontWeight: '400',
              lineHeight: '28px',
              letterSpacing: '1px',
            }}
          >
            {t('resources.dashboard.welcome.teams')}
          </Typography>
          <Count
            resource="teams"
            filter={{}}
            variant="span"
            sx={{
              fontSize: '16px',
              fontWeight: '700',
              lineHeight: '28px',
              letterSpacing: '1px',
              color: customPalette.greyDark,
            }}
          />
        </Box>
      ) : null}

      {hasRole(['redactor']) && !hasRole(['editor']) && userData.points ? (
        <Box
          variant="h3"
          sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}
        >
          <Typography
            variant="span"
            sx={{
              fontSize: '18px',
              fontWeight: '400',
              lineHeight: '28px',
              letterSpacing: '1px',
            }}
          >
            {t('resources.dashboard.welcome.points')}
          </Typography>
          <Typography
            variant="span"
            sx={{
              fontSize: '16px',
              fontWeight: '700',
              lineHeight: '28px',
              letterSpacing: '1px',
              color: customPalette.greyDark,
            }}
          >
            {userData.points}
          </Typography>
        </Box>
      ) : null}
      {hasRole(['redactor']) || hasRole(['editor']) ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: '400',
                lineHeight: '28px',
                letterSpacing: '1px',
                color: customPalette.greyDark,
              }}
            >
              {t('resources.dashboard.welcome.team')}
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '700',
                lineHeight: '28px',
                letterSpacing: '1px',
                color: customPalette.greyDark,
              }}
            >
              {teamName}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: '400',
                lineHeight: '28px',
                letterSpacing: '1px',
                color: customPalette.greyDark,
              }}
            >
              {t('resources.dashboard.welcome.role')}
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '700',
                lineHeight: '28px',
                letterSpacing: '1px',
                color: customPalette.greyDark,
                transform: 'capitalize',
              }}
            >
              {userData.roles.join(', ')}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {hasRole(['admin', 'gerente', 'editor']) ? (
        <Box
          variant="h3"
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '18px',
            fontWeight: '400',
            lineHeight: '28px',
            letterSpacing: '1px',
            color: customPalette.greyDark,
          }}
        >
          <Typography variant="span">
            {t('resources.dashboard.welcome.members')}
          </Typography>
          {totalMembers ? (
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: '700',
                lineHeight: '28px',
                letterSpacing: '1.5px',
                color: customPalette.greyDark,
              }}
            >
              {totalMembers}
            </Typography>
          ) : (
            <Count
              resource="members"
              filter={{}}
              variant="span"
              sx={{
                fontSize: '18px',
                fontWeight: '700',
                lineHeight: '28px',
                letterSpacing: '1.5px',
                color: customPalette.greyDark,
              }}
            />
          )}
        </Box>
      ) : null}
    </Box>
  );
};

const WelcomeAvatar = _props => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  return (
    <Box display="flex" flexDirection="row" gap={3}>
      <Box>
        <Avatar
          src={identity?.avatar ? `${identity.avatar}` : null}
          style={{ width: 70, height: 70 }}
        />
      </Box>
      <Box>
        {identity ? (
          <Typography variant="h5" component="h2" gutterBottom>
            {translate('resources.dashboard.welcome.title', {
              name: identity.fullName ?? 'a ScuoreCard',
            })}
          </Typography>
        ) : null}
        <Box maxWidth="40em">
          <MoreInformation />
        </Box>
        <LastUpdated />
      </Box>
    </Box>
  );
};

const GoalStatus = ({ loading, unless_goal, percent_completed, trend }) => {
  const t = useTranslate();
  return (
    <Box gap={2} display={{ xs: 'block', sm: 'flex' }}>
      <StatusCircle value={percent_completed} />
      <Box display="flex" flexDirection="column">
        <Box variant="h1" sx={{ fontSize: '32px', fontWeight: 'bold' }}>
          {percent_completed} %
        </Box>
        <Box
          variant="h2"
          sx={{ minWidth: '100px', fontSize: '16px', fontWeight: '400' }}
        >
          {loading
            ? ''
            : unless_goal
              ? t('resources.dashboard.welcome.unless_goal')
              : t('resources.dashboard.welcome.percentage_completed')}
        </Box>
        {trend ? <TrendingIndicator trend="up" value={10} /> : null}
      </Box>
    </Box>
  );
};

const Welcome = ({ metrics, loading }) => {
  return (
    <Card
      elevation={0}
      sx={{
        background: '#fff',
        color: '#000',
        padding: '20px',
        marginTop: 2,
        marginBottom: '1em',
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <WelcomeAvatar />
        <Box display="flex" flexDirection="row">
          <GoalStatus
            loading={loading}
            unless_goal={metrics && metrics.length === 0}
            percent_completed={metrics?.percentage ?? 0}
          />
        </Box>
      </Box>
    </Card>
  );
};

export default Welcome;
